import axios from 'axios';
import axiosInstance from './axiosCreator';
import type { Block, BlockType } from '../types/Study';
import type {
  ItemComponentScore,
  ItemResponsePayload,
  ItemSpec,
  ItemType,
  ClientRecord,
  VignetteContent,
  Vignette,
  VignetteResponse,
  VignettePresentedVariant,
  VignetteVariantView,
} from '../types/Vignette';

type QbankBlockType = 'tutor' | 'timed';
type QbankAttributeFilter = 'unseen' | 'incorrect' | 'marked' | 'all';
export type TopicTreeQbankStatusMode = 'new' | 'due' | 'newAndDue' | 'viewAll';
export type QbankSchemaVersion = 'v1' | 'v2';

interface QbankBlockPayload {
  type?: QbankBlockType;
  size?: number;
}

interface QbankFilterSetPayload {
  schemaVersion?: QbankSchemaVersion;
  contentType: 'qbank';
  filterType?: 'custom' | 'session' | 'topic';
  exams?: { id: string; name?: string }[];
  topics?: { id: string; name?: string }[];
  lectures?: { id: string; name?: string }[];
  studySessionId?: string;
  count: number;
  qbank: {
    attribute?: QbankAttributeFilter;
    statusMode?: TopicTreeQbankStatusMode;
    viewAllCursor?: string;
  };
}

export interface CreateQbankBlockRequest {
  block?: QbankBlockPayload;
  filters: QbankFilterSetPayload;
}

export interface QbankCountRequest {
  examId?: string;
  examIds?: string[];
  topicIds?: string[];
  lectureIds?: string[];
  studySessionId?: string;
}

export interface QbankCountSnapshot {
  total: number;
  new: number;
  due: number;
  scheduled: number;
  complete: number;
  // Started-but-not-submitted blocks for this scope. Optional so the client
  // tolerates older backends that don't yet send it.
  inProgress?: number;
}

export interface QbankBlockResponse {
  block: Block;
  vignettes?: Vignette[];
  filterSet?: Record<string, unknown>;
}

type QbankV2SchedulingState = 'NEW' | 'DUE' | 'COMPLETE' | 'EXHAUSTED';

type QbankV2AnswerOptionDto = {
  id: string;
  ord?: number;
  letter: string;
  optionText: string;
  isCorrect: boolean;
  explanationMain: string;
  explanationTutor: string | null;
  selectionCount: number;
};

type QbankV2VariantDto = {
  id: string;
  ord: number;
  question: string;
  keyPoint: string | null;
  learningObjective?: string | null;
  references: string[];
  correctAnswer?: string;
  explanation?: string;
  versionNum?: number;
  isQAed?: boolean;
  answerOptions: QbankV2AnswerOptionDto[];
  imageLinks?: unknown[];
  explainerLinks?: Array<{ explainerId: string; ord: number | null }>;
  timing?: QbankV2TimingDto | null;
  // NCLEX item types (ora-be docs/nclex-item-api.md).
  itemType?: ItemType;
  itemSpec?: ItemSpec | null;
  // Chart/exhibit, trend or case-study record (ORA-1127); any item type.
  clientRecord?: ClientRecord | null;
};

type QbankV2ResponseDto = {
  id: string;
  correct: boolean | null;
  answer: string;
  correctAnswer: string | null;
  luckyGuess?: boolean;
  isSubmitted: boolean;
  submissionDate?: string | null;
  attemptNumber: number;
  createdAt: string;
  updatedAt: string;
  blockId?: string | null;
  mode?: string | null;
  isRetry?: boolean;
  // NCLEX item types only.
  response?: ItemResponsePayload | string | null;
  pointsEarned?: number | null;
  maxPoints?: number | null;
  perComponent?: ItemComponentScore[] | null;
};

type QbankV2TimingDto = {
  id: string;
  firstSeenAt?: string;
  submittedAt?: string;
  studyMs?: number;
  reviewMs?: number;
  answerStudyMs?: number | null;
  lastStudyHeartbeatAt?: string;
  lastReviewHeartbeatAt?: string;
  activityDate?: string;
};

type QbankV2VignetteDto = {
  id: string;
  schemaVersion: 'v2';
  yield?: number;
  examLinks: Array<{ examId: string; yield: number | null }>;
  scheduling: {
    state: QbankV2SchedulingState | number;
    dueAt: string | null;
    dueStep: number;
    nextVariantOrd: number | null;
    questionHighlights: string | null;
    answerCrossouts: boolean[];
    flagged: boolean;
    notes: string | null;
  };
  presentedVariantId: string;
  presentedVariant: QbankV2VariantDto | null;
  nextVariantPreview?: QbankV2VariantDto | null;
  response?: QbankV2ResponseDto | null;
  responses?: QbankV2ResponseDto[];
  variantViews?: Array<{
    variant: QbankV2VariantDto;
    response?: QbankV2ResponseDto | null;
    scheduling: {
      questionHighlights: string | null;
      answerCrossouts: boolean[];
      flagged: boolean;
      notes: string | null;
    };
    isAnswerable: boolean;
    isReviewOnly: boolean;
  }>;
  timing?: QbankV2TimingDto | null;
  articles?: Array<{ id: string; slug: string; title: string }>;
  isSuspended?: boolean;
  suspendReasons?: string[];
  isQAed?: boolean;
  createdAt?: string;
  // NCLEX Format tour blocks only (ORA-1127).
  formatLabel?: string | null;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const unwrapResponsePayload = (
  payload: unknown
): Record<string, unknown> | null => {
  if (!isRecord(payload)) return null;
  if ('block' in payload) return payload;
  if ('data' in payload) {
    return unwrapResponsePayload(payload.data);
  }
  return payload;
};

const isBlockLike = (value: unknown): value is Block =>
  isRecord(value) && typeof value.id === 'string';

const isQbankV2Vignette = (value: unknown): value is QbankV2VignetteDto =>
  isRecord(value) &&
  value.schemaVersion === 'v2' &&
  typeof value.presentedVariantId === 'string' &&
  'presentedVariant' in value;

const isBlockType = (value: string): value is BlockType =>
  value === 'tutor' || value === 'timed';

const toLegacySchedulingState = (state: QbankV2SchedulingState): number => {
  switch (state) {
    case 'DUE':
      return 1;
    case 'COMPLETE':
      return 2;
    case 'EXHAUSTED':
      return 3;
    case 'NEW':
    default:
      return 0;
  }
};

const sortAnswerOptionsByLetter = <T extends { letter: string }>(
  options: T[]
) =>
  [...options].sort((left, right) => left.letter.localeCompare(right.letter));

const toLegacyResponse = (
  response?: QbankV2ResponseDto | null
): VignetteResponse | undefined => {
  if (!response) return undefined;

  const mode =
    response.mode && isBlockType(response.mode) ? response.mode : undefined;

  return {
    id: response.id,
    correct: Boolean(response.correct),
    answer: response.answer,
    correctAnswer: response.correctAnswer ?? '',
    ...(response.luckyGuess !== undefined
      ? { luckyGuess: response.luckyGuess }
      : {}),
    isSubmitted: response.isSubmitted,
    ...(response.submissionDate !== undefined
      ? { submissionDate: response.submissionDate }
      : {}),
    attemptNumber: response.attemptNumber,
    createdAt: response.createdAt,
    updatedAt: response.updatedAt,
    ...(response.blockId ? { blockId: response.blockId } : {}),
    ...(mode ? { mode } : {}),
    ...(response.isRetry !== undefined ? { isRetry: response.isRetry } : {}),
    // Item-type fields ride along only when present, so a multiple-choice
    // row normalizes exactly as before.
    ...(response.response != null ? { response: response.response } : {}),
    ...(response.pointsEarned !== undefined
      ? { pointsEarned: response.pointsEarned }
      : {}),
    ...(response.maxPoints !== undefined
      ? { maxPoints: response.maxPoints }
      : {}),
    ...(response.perComponent !== undefined
      ? { perComponent: response.perComponent }
      : {}),
  };
};

const mapQbankV2PresentedVariantToContent = (
  presentedVariant: QbankV2VariantDto
): VignettePresentedVariant => {
  const sortedAnswerOptions = sortAnswerOptionsByLetter(
    presentedVariant.answerOptions
  );

  const correctOption = sortedAnswerOptions.find((option) => option.isCorrect);

  return {
    id: presentedVariant.id,
    question: presentedVariant.question,
    answerOptions: sortedAnswerOptions.map((option) => ({
      id: option.id,
      letter: option.letter,
      optionText: option.optionText,
      isCorrect: option.isCorrect,
      explanationMain: option.explanationMain,
      explanationTutor: option.explanationTutor,
      selectionCount: option.selectionCount,
      ord: option.ord,
    })),
    correctAnswer:
      presentedVariant.correctAnswer ?? correctOption?.letter ?? '',
    explanation:
      presentedVariant.explanation ??
      correctOption?.explanationMain ??
      sortedAnswerOptions[0]?.explanationMain ??
      '',
    learningObjective:
      presentedVariant.learningObjective ?? presentedVariant.keyPoint ?? '',
    references: presentedVariant.references ?? [],
    versionNum: presentedVariant.versionNum ?? presentedVariant.ord,
    keyPoint: presentedVariant.keyPoint,
    ord: presentedVariant.ord,
    exhibit: '',
    isQAed: Boolean(presentedVariant.isQAed),
    imageLinks: presentedVariant.imageLinks as VignetteContent['imageLinks'],
    explainerLinks: presentedVariant.explainerLinks ?? [],
    // Preserve per-variant timing through the block-fetch normalizer so the
    // "time to answer" badge survives a re-fetch (reload / re-entering review),
    // not just the raw answer-response payload.
    timing: presentedVariant.timing ?? null,
    ...(presentedVariant.itemType
      ? { itemType: presentedVariant.itemType }
      : {}),
    ...(presentedVariant.itemSpec != null
      ? { itemSpec: presentedVariant.itemSpec }
      : {}),
    ...(presentedVariant.clientRecord != null
      ? { clientRecord: presentedVariant.clientRecord }
      : {}),
  };
};

const mapQbankV2VignetteToRuntimeVignette = (
  vignette: QbankV2VignetteDto
): Vignette => {
  const isPresentedVariantConsistent =
    vignette.presentedVariant?.id === vignette.presentedVariantId;

  if (!isPresentedVariantConsistent) {
    throw new Error(
      `Invalid V2 vignette payload for ${vignette.id}: missing or inconsistent presentedVariant`
    );
  }

  const presentedVariant = mapQbankV2PresentedVariantToContent(
    vignette.presentedVariant!
  );

  const responses = vignette.responses
    ?.map((response) => toLegacyResponse(response))
    .filter((response): response is VignetteResponse => Boolean(response));

  const response = toLegacyResponse(vignette.response);

  const variantViews = vignette.variantViews
    ?.map((variantView): VignetteVariantView => ({
      variant: mapQbankV2PresentedVariantToContent(variantView.variant),
      response: toLegacyResponse(variantView.response) ?? null,
      scheduling: {
        questionHighlights:
          variantView.scheduling.questionHighlights ?? undefined,
        answerCrossouts: variantView.scheduling.answerCrossouts,
        flagged: variantView.scheduling.flagged,
        notes: variantView.scheduling.notes ?? undefined,
      },
      isAnswerable: variantView.isAnswerable,
      isReviewOnly: variantView.isReviewOnly,
    }))
    .filter(Boolean);

  return {
    id: vignette.id,
    schemaVersion: 'v2',
    yield: vignette.yield ?? vignette.examLinks[0]?.yield ?? 0,
    presentedVariantId: vignette.presentedVariantId,
    presentedVariant,
    nextVariantPreview: vignette.nextVariantPreview
      ? mapQbankV2PresentedVariantToContent(vignette.nextVariantPreview)
      : null,
    scheduling: {
      state:
        typeof vignette.scheduling.state === 'number'
          ? vignette.scheduling.state
          : toLegacySchedulingState(vignette.scheduling.state),
      dueAt: vignette.scheduling.dueAt,
      dueStep: vignette.scheduling.dueStep,
      nextVariantOrd: vignette.scheduling.nextVariantOrd,
      questionHighlights: vignette.scheduling.questionHighlights ?? undefined,
      answerCrossouts: vignette.scheduling.answerCrossouts,
      flagged: vignette.scheduling.flagged,
      notes: vignette.scheduling.notes ?? undefined,
    },
    articles: vignette.articles,
    isSuspended: vignette.isSuspended,
    suspendReasons: vignette.suspendReasons,
    isQAed: Boolean(vignette.isQAed),
    createdAt: vignette.createdAt,
    response,
    responses: responses?.length ? responses : undefined,
    variantViews: variantViews?.length ? variantViews : undefined,
    timing: vignette.timing ?? null,
    ...(vignette.formatLabel ? { formatLabel: vignette.formatLabel } : {}),
  };
};

export const normalizeQbankBlockResponse = (
  payload: unknown
): QbankBlockResponse | null => {
  const normalized = unwrapResponsePayload(payload);
  if (!normalized) return null;

  const candidateBlock =
    'block' in normalized && isRecord(normalized.block)
      ? normalized.block
      : normalized;

  if (!isBlockLike(candidateBlock)) {
    return null;
  }

  const rawVignettes =
    Array.isArray(normalized.vignettes) && normalized.vignettes.length > 0
      ? normalized.vignettes
      : undefined;

  const normalizedVignettes = rawVignettes?.map((vignette) =>
    isQbankV2Vignette(vignette)
      ? mapQbankV2VignetteToRuntimeVignette(vignette)
      : (vignette as Vignette)
  );

  return {
    block: {
      ...candidateBlock,
      ...(isRecord(normalized.filterSet)
        ? { filterSet: normalized.filterSet as Block['filterSet'] }
        : {}),
    },
    vignettes: normalizedVignettes,
    filterSet: isRecord(normalized.filterSet)
      ? normalized.filterSet
      : undefined,
  };
};

export const createQbankBlockFromFilters = async (
  payload: CreateQbankBlockRequest
) => {
  try {
    const response = await axiosInstance.post('/api/v5/qbank/blocks', {
      ...payload,
      filters: {
        ...payload.filters,
        schemaVersion: payload.filters.schemaVersion ?? 'v2',
      },
    });

    return response.data;
  } catch (error) {
    console.error('Error creating qbank block from filters:', error);
    throw error;
  }
};

export const fetchQbankCounts = async (payload: QbankCountRequest) => {
  try {
    const response = await axiosInstance.post('/api/v5/qbank/count', payload);
    return response.data;
  } catch (error) {
    console.error('Error fetching qbank counts:', error);
    throw error;
  }
};

export const getQbankBlockById = async (blockId: string) => {
  try {
    const response = await axiosInstance.get(`/api/v5/qbank/blocks/${blockId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching qbank block:', error);
    throw error;
  }
};

export const getQbankBlocks = async ({
  page = 1,
  limit = 10,
}: {
  page?: number;
  limit?: number;
} = {}) => {
  try {
    const response = await axiosInstance.get('/api/v5/qbank/blocks', {
      params: { page, limit },
    });

    return response.data;
  } catch (error) {
    console.error('Error fetching qbank blocks:', error);
    throw error;
  }
};

export const getQbankTopicBlocks = async ({
  topicId,
  examId,
  page = 1,
  limit = 10,
}: {
  topicId: string;
  examId?: string;
  page?: number;
  limit?: number;
}) => {
  try {
    const response = await axiosInstance.get('/api/v5/qbank/blocks/topic', {
      params: {
        topicId,
        ...(examId ? { examId } : {}),
        page,
        limit,
      },
    });

    return response.data;
  } catch (error) {
    console.error('Error fetching topic qbank blocks:', error);
    throw error;
  }
};

export const getQbankLectureBlocks = async ({
  lectureIds,
  page = 1,
  limit = 10,
}: {
  lectureIds: string[];
  page?: number;
  limit?: number;
}) => {
  try {
    const response = await axiosInstance.get('/api/v5/qbank/blocks/lectures', {
      params: {
        lectureIds: lectureIds.join(','),
        page,
        limit,
      },
    });

    return response.data;
  } catch (error) {
    console.error('Error fetching lecture qbank blocks:', error);
    throw error;
  }
};

export const getQbankSessionBlocks = async ({
  studySessionId,
  page = 1,
  limit = 10,
}: {
  studySessionId: string;
  page?: number;
  limit?: number;
}) => {
  try {
    const response = await axiosInstance.get('/api/v5/qbank/blocks/session', {
      params: {
        studySessionId,
        page,
        limit,
      },
    });

    return response.data;
  } catch (error) {
    console.error('Error fetching session qbank blocks:', error);
    throw error;
  }
};

/**
 * The QBank variant this student has queued for a vignette, if any (ORA-830).
 *
 * The same variant the answer response hands back on a miss
 * (`submitChatVignetteAnswer`), read on demand instead — which is what lets a
 * surface that was not the one that answered (a question inside a study set, or
 * any question after a reload) draw the "Scheduled 1 QBank Question" badge.
 *
 * Resolves to null rather than throwing: nothing queued, a vignette the student
 * cannot see, and an older backend that does not serve this route yet all mean
 * the same thing to the badge — there is nothing to offer.
 */
export const fetchScheduledVariantV5 = async (
  vignetteId: string
): Promise<{
  content: VignettePresentedVariant;
  dueAt: string | null;
} | null> => {
  try {
    const response = await axiosInstance.get(
      `/api/v5/qbank/vignettes/${vignetteId}/scheduled-variant`
    );

    const variant = response.data?.variant;
    if (!variant || typeof variant !== 'object') return null;

    const dueAt = response.data?.dueAt;

    return {
      content: variant as VignettePresentedVariant,
      dueAt: typeof dueAt === 'string' ? dueAt : null,
    };
  } catch (error) {
    // A 404 is the ordinary answer from a backend that does not serve this
    // route yet, and from a vignette this student cannot see; neither is worth
    // a console line on every missed question.
    if (!axios.isAxiosError(error) || error.response?.status !== 404) {
      console.error('Error fetching the scheduled variant:', error);
    }

    return null;
  }
};

/** One vignette variant as this student sees it, with their recorded answer. */
export type VignetteQuestionV5 = {
  variant: VignettePresentedVariant;
  /** The student's latest response on this variant; null when unanswered. */
  response: VignetteResponse | null;
};

export type VignetteQuestionV5Result =
  | { status: 'ok'; question: VignetteQuestionV5 }
  | { status: 'not_found' }
  /** A free student at today's QBank limit, on a variant not yet answered. */
  | { status: 'limit_reached' }
  /** Mid self-assessment: the DTO carries the key, so the server refuses. */
  | { status: 'self_assessment_in_progress' }
  | { status: 'error' };

/**
 * A single question opened from a link (ORA-948: the activation nudge email):
 * the vignette shown as the named variant, in the same runtime DTO the answer
 * route returns (`presentedVariant` + this student's `response`).
 *
 * A 404, or a payload without the variant, is `not_found` (a stale or foreign
 * link). A 429 `DAILY_QBANK_LIMIT` and a 409 `SELF_ASSESSMENT_IN_PROGRESS` are
 * their own outcomes; anything else that fails is `error`, which a retry may
 * fix.
 */
export const fetchVignetteQuestionV5 = async (
  vignetteId: string,
  variantId: string
): Promise<VignetteQuestionV5Result> => {
  try {
    const response = await axiosInstance.get<{ data?: Vignette }>(
      `/api/v5/qbank/vignettes/${encodeURIComponent(vignetteId)}`,
      { params: { variantId } }
    );

    const vignette = response.data?.data;
    const variant = vignette?.presentedVariant;
    if (!variant || variant.id !== variantId) return { status: 'not_found' };

    return {
      status: 'ok',
      question: { variant, response: vignette.response ?? null },
    };
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const code = error.response?.data?.code;

      if (error.response?.status === 404) return { status: 'not_found' };

      if (code === 'DAILY_QBANK_LIMIT') return { status: 'limit_reached' };

      if (code === 'SELF_ASSESSMENT_IN_PROGRESS') {
        return { status: 'self_assessment_in_progress' };
      }
    }

    console.error('Error fetching the vignette question:', error);
    return { status: 'error' };
  }
};
